/* faith-hero-gold-dust.js — Hero video resume + tap-to-resume fallback.

   iOS Safari (15.1 → 26.x) freezes hero videos on background→foreground
   transitions. Even when the media element reports paused=false and
   currentTime advances, the compositor keeps a stale frame. No paint hint
   (transform, display, will-change) fixes the existing element. The
   reliable fix is to clone the <video> element and replace it — iOS
   cannot keep a stale frame on a brand-new element.

   We do that on every resume signal (focus / visibilitychange /
   pageshow(persisted) / resume). A 500ms polling loop reflects the video's
   paused state into a .is-paused class on the hero, which reveals a
   tap-to-resume overlay as a last-line fallback. */

(function () {
  'use strict';

  // The Theme Editor re-renders the hero section on every edit, handing us a
  // brand-new <section>/<video> pair. `timer` and `live` let a re-init adopt
  // the new nodes without leaving the previous instance's polling loop running
  // against a detached element.
  var timer = null;
  var live = null;
  var resumeSignalsBound = false;

  function init() {
    var hero = document.querySelector('.faith-hero-gd');
    if (!hero) { live = null; return; }
    var video = hero.querySelector('.faith-hero-gd__video');
    if (!video) { live = null; return; }

    if (timer) clearInterval(timer);

    var armedForRepaint = false;

    function sync() {
      // .is-playing fades the still out to reveal the live video. Driven by the
      // real playing state, so the still covers the video whenever it isn't
      // actually playing — including iOS Low Power Mode, where it never plays
      // and the still simply stays (hiding the native <video> control).
      if (video.paused || video.ended) hero.classList.remove('is-playing');
      else hero.classList.add('is-playing');
    }

    function cloneAndReplace() {
      var parent = video.parentNode;
      if (!parent) return;
      var oldTime = video.currentTime;
      var clone = video.cloneNode(true);
      clone.addEventListener('loadedmetadata', function () {
        try { clone.currentTime = oldTime; } catch (e) {}
        var p = clone.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }, { once: true });
      parent.replaceChild(clone, video);
      var p = clone.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
      video = clone;
    }

    function softPlay() {
      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () {
          if (armedForRepaint) {
            armedForRepaint = false;
            cloneAndReplace();
          }
        }).catch(function () {});
      }
    }

    function armRepaint() { armedForRepaint = true; }

    live = { armRepaint: armRepaint, softPlay: softPlay };
    bindResumeSignals();

    // No tap-to-resume. If autoplay is blocked (iOS Low Power Mode), the still
    // simply stays. The video still auto-recovers on the legitimate resume
    // signals above (returning to the tab / bfcache restore) when the OS allows
    // playback again — but we never prompt the visitor to tap.

    // Sync on video's own state events
    video.addEventListener('playing', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('ended', sync);

    // 500ms polling loop — source of truth for the is-paused class on iOS,
    // where pause/playing events don't always fire after a background→
    // foreground transition.
    timer = setInterval(sync, 500);

    // Initial state
    softPlay();
    sync();
  }

  // These live on document/window rather than the hero, so they're bound once
  // and routed to whichever hero instance is currently mounted.
  function bindResumeSignals() {
    if (resumeSignalsBound) return;
    resumeSignalsBound = true;

    // Resume signals — arm a clone-and-replace, then try to play
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && live) { live.armRepaint(); live.softPlay(); }
    });
    window.addEventListener('focus', function () {
      if (live) { live.armRepaint(); live.softPlay(); }
    });
    window.addEventListener('pageshow', function (e) {
      if (!live) return;
      if (e.persisted) live.armRepaint();
      live.softPlay();
    });
    document.addEventListener('resume', function () {
      if (live) { live.armRepaint(); live.softPlay(); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.faith-hero-gd')) init();
  });
})();
